/**
 * DiagnosisCard Component
 * Automated performance diagnosis with actionable insights
 * Created: 2025-11-17
 */

import React, { useMemo } from 'react';
import {
  calculate6MonthRollingAverage,
  calculateCurrentMonthRates,
  compareMetrics,
  calculateConversionRates,
  generateAutomatedDiagnosis,
  getLastNCompleteMonths
} from '../utils/trendingCalculations';

export default function DiagnosisCard({ data, selectedRep, month, reps, developerMode }) {
  // Calculate business days elapsed
  const businessDaysElapsed = useMemo(() => {
    const today = new Date();
    const year = today.getFullYear();
    const currentMonth = today.getMonth();

    let businessDays = 0;
    for (let day = 1; day <= today.getDate(); day++) {
      const date = new Date(year, currentMonth, day);
      const dayOfWeek = date.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        businessDays++;
      }
    }
    return businessDays;
  }, []);

  // Current month rates
  const currentRates = useMemo(() => {
    return calculateCurrentMonthRates(data, selectedRep, month, businessDaysElapsed);
  }, [data, selectedRep, month, businessDaysElapsed]);

  // Historical averages
  const historicalAverages = useMemo(() => {
    const metrics = [
      { key: 'callsPerDay', tab: 'Dials_TalkTime', field: null },
      { key: 'smsPerDay', tab: 'SMS', field: 'Count' },
      { key: 'appsPerDay', tab: 'Apps_In', field: null },
      { key: 'approvedPerDay', tab: 'Approved', field: null },
      { key: 'fundedPerDay', tab: 'Funded', field: null },
      { key: 'revenuePerDay', tab: 'Revenue', field: 'Revenue' }
    ];

    const averages = {};

    metrics.forEach(({ key, tab, field }) => {
      const result = calculate6MonthRollingAverage(data, selectedRep, month, { tab, field });
      averages[key] = result.avgPerDay;
    });

    return averages;
  }, [data, selectedRep, month]);

  // Comparisons
  const comparisons = useMemo(() => {
    return {
      callsPerDay: compareMetrics(currentRates.callsPerDay, historicalAverages.callsPerDay),
      smsPerDay: compareMetrics(currentRates.smsPerDay, historicalAverages.smsPerDay),
      appsPerDay: compareMetrics(currentRates.appsPerDay, historicalAverages.appsPerDay),
      approvedPerDay: compareMetrics(currentRates.approvedPerDay, historicalAverages.approvedPerDay),
      fundedPerDay: compareMetrics(currentRates.fundedPerDay, historicalAverages.fundedPerDay),
      revenuePerDay: compareMetrics(currentRates.revenuePerDay, historicalAverages.revenuePerDay)
    };
  }, [currentRates, historicalAverages]);

  // Conversion rates
  const conversionRates = useMemo(() => {
    const current = calculateConversionRates(data, selectedRep, month);

    // Calculate 6-month average conversion rates
    const last6Months = getLastNCompleteMonths(month, 6);
    let totalCallsToApps = 0;
    let totalApprovedToFunded = 0;
    let monthsCount = 0;

    last6Months.forEach(m => {
      const rates = calculateConversionRates(data, selectedRep, m);
      if (rates.counts.apps > 0) {
        totalCallsToApps += rates.callsToApps;
        totalApprovedToFunded += rates.approvedToFunded;
        monthsCount++;
      }
    });

    const historical = {
      callsToApps: monthsCount > 0 ? totalCallsToApps / monthsCount : 0,
      approvedToFunded: monthsCount > 0 ? totalApprovedToFunded / monthsCount : 0
    };

    return { current, historical };
  }, [data, selectedRep, month]);

  // Generate diagnosis
  const diagnosis = useMemo(() => {
    return generateAutomatedDiagnosis(comparisons, conversionRates);
  }, [comparisons, conversionRates]);

  return (
    <div style={{
      backgroundColor: 'white',
      padding: '20px',
      borderRadius: '8px',
      marginBottom: '20px',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
      border: '1px solid #e2e8f0',
      position: 'relative'
    }}>
      {/* Developer Mode: Section Label */}
      {developerMode && (
        <div className="dev-section-label">S-4</div>
      )}
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: '16px',
        paddingBottom: '12px',
        borderBottom: '2px solid #e2e8f0'
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ fontSize: '20px', marginRight: '8px' }}>💡</span>
            <h3 style={{
              margin: 0,
              fontSize: '18px',
              fontWeight: 700,
              color: '#1e293b'
            }}>
              What's Different & Why
            </h3>
          </div>
          <div style={{
            fontSize: '12px',
            color: '#64748b',
            marginTop: '4px',
            marginLeft: '28px'
          }}>
            Compared to your 6-month average • Automated insights
          </div>
        </div>
      </div>

      {/* Positive Changes */}
      {diagnosis.positiveChanges.length > 0 && (
        <div style={{
          marginBottom: '16px',
          padding: '12px',
          backgroundColor: '#f0fdf4',
          border: '1px solid #86efac',
          borderRadius: '6px'
        }}>
          <div style={{
            fontSize: '14px',
            fontWeight: 700,
            color: '#166534',
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center'
          }}>
            <span style={{ marginRight: '6px' }}>✅</span>
            POSITIVE CHANGES:
          </div>
          <ul style={{
            margin: 0,
            paddingLeft: '20px',
            listStyleType: 'disc'
          }}>
            {diagnosis.positiveChanges.map((change, idx) => (
              <li key={idx} style={{
                fontSize: '13px',
                color: '#15803d',
                marginBottom: '4px',
                lineHeight: '1.5'
              }}>
                <strong>{change.metric}</strong> {change.description} ({change.percent})
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Negative Changes / Areas of Concern */}
      {diagnosis.negativeChanges.length > 0 && (
        <div style={{
          marginBottom: '16px',
          padding: '12px',
          backgroundColor: '#fef2f2',
          border: '1px solid #fca5a5',
          borderRadius: '6px'
        }}>
          <div style={{
            fontSize: '14px',
            fontWeight: 700,
            color: '#991b1b',
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center'
          }}>
            <span style={{ marginRight: '6px' }}>🔴</span>
            AREAS OF CONCERN:
          </div>
          <ul style={{
            margin: 0,
            paddingLeft: '20px',
            listStyleType: 'disc'
          }}>
            {diagnosis.negativeChanges.map((change, idx) => (
              <li key={idx} style={{
                fontSize: '13px',
                color: '#b91c1c',
                marginBottom: '4px',
                lineHeight: '1.5'
              }}>
                <strong>{change.metric}</strong> {change.description} ({change.percent})
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Overall Diagnosis */}
      <div style={{
        marginBottom: '16px',
        padding: '12px',
        backgroundColor: '#f8fafc',
        border: '1px solid #cbd5e1',
        borderRadius: '6px'
      }}>
        <div style={{
          fontSize: '14px',
          fontWeight: 700,
          color: '#475569',
          marginBottom: '8px',
          display: 'flex',
          alignItems: 'center'
        }}>
          <span style={{ marginRight: '6px' }}>📊</span>
          DIAGNOSIS:
        </div>
        <div style={{
          fontSize: '13px',
          color: '#1e293b',
          lineHeight: '1.6'
        }}>
          {diagnosis.diagnosis}
        </div>
      </div>

      {/* Action Items */}
      {diagnosis.actionItems.length > 0 && (
        <div style={{
          padding: '12px',
          backgroundColor: '#eff6ff',
          border: '1px solid #93c5fd',
          borderRadius: '6px'
        }}>
          <div style={{
            fontSize: '14px',
            fontWeight: 700,
            color: '#1e40af',
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center'
          }}>
            <span style={{ marginRight: '6px' }}>🎯</span>
            ACTION ITEMS:
          </div>
          <ol style={{
            margin: 0,
            paddingLeft: '20px',
            listStyleType: 'decimal'
          }}>
            {diagnosis.actionItems.map((action, idx) => (
              <li key={idx} style={{
                fontSize: '13px',
                color: '#1e40af',
                marginBottom: '4px',
                lineHeight: '1.5'
              }}>
                {action}
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Stable Performance Message */}
      {diagnosis.positiveChanges.length === 0 && diagnosis.negativeChanges.length === 0 && (
        <div style={{
          padding: '12px',
          backgroundColor: '#f8fafc',
          border: '1px solid #cbd5e1',
          borderRadius: '6px',
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: '16px',
            color: '#64748b',
            marginBottom: '4px'
          }}>
            →
          </div>
          <div style={{
            fontSize: '14px',
            fontWeight: 600,
            color: '#475569'
          }}>
            Performance is Stable
          </div>
          <div style={{
            fontSize: '13px',
            color: '#64748b',
            marginTop: '4px'
          }}>
            All key metrics are within expected ranges compared to 6-month average
          </div>
        </div>
      )}
    </div>
  );
}
